import { useState, useMemo, useEffect } from 'react';
import { useCustomersSaas } from '@/hooks/useCustomersSaas';
import { useProducts } from '@/hooks/useProducts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, Users, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CustomerSaasDialog } from '@/components/customers-saas/CustomerSaasDialog';
import { StatusInfoDialog } from '@/components/customers-saas/StatusInfoDialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { calculateCustomerStatus } from '@/utils/customerStatusCalculator';

export default function CustomersSaas() {
  const { customers, isLoading } = useCustomersSaas();
  const { products } = useProducts();
  const [searchTerm, setSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Agrupar clientes por nome para evitar duplicação no filtro
  const uniqueCustomers = useMemo(() => {
    if (!customers) return [];
    
    const customerMap = new Map<string, any>();
    
    customers.forEach(customer => {
      if (!customerMap.has(customer.name)) {
        customerMap.set(customer.name, customer);
      }
    });
    
    return Array.from(customerMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [customers]);

  useEffect(() => {
    document.title = 'Dashi | Clientes SaaS';
  }, []);

  const filteredCustomers = customers?.filter((customer) => {
    const matchesSearch = 
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.product?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = productFilter === 'all' || customer.product?.id === productFilter;
    const matchesCustomer = customerFilter === 'all' || customer.name === customerFilter;
    
    // Calcular status para filtro
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const calculatedStatus = calculateCustomerStatus({
        lastTransactionDate: customer.last_transaction_date,
        periodEndDate: customer.last_transaction?.[0]?.period_end_date || null,
        recurrenceType: customer.last_transaction?.[0]?.product_plan?.recurrence_type as any || null,
      });
      matchesStatus = calculatedStatus.label === statusFilter;
    }
    
    return matchesSearch && matchesProduct && matchesCustomer && matchesStatus;
  });

  const sortedCustomers = useMemo(() => {
    if (!sortConfig) return filteredCustomers;

    const sorted = [...(filteredCustomers || [])].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'product':
          aValue = a.product?.name || '';
          bValue = b.product?.name || '';
          break;
        case 'city':
          aValue = a.city || '';
          bValue = b.city || '';
          break;
        case 'state':
          aValue = a.state || '';
          bValue = b.state || '';
          break;
        case 'renewals':
          aValue = a.total_renewals || 0;
          bValue = b.total_renewals || 0;
          break;
        case 'firstPurchase':
          aValue = a.first_purchase_date ? new Date(a.first_purchase_date).getTime() : 0;
          bValue = b.first_purchase_date ? new Date(b.first_purchase_date).getTime() : 0;
          break;
        case 'lastTransaction':
          aValue = a.last_transaction_date ? new Date(a.last_transaction_date).getTime() : 0;
          bValue = b.last_transaction_date ? new Date(b.last_transaction_date).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredCustomers, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  const handleEdit = (customer: any) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedCustomer(null);
    setDialogOpen(true);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8" />
            Clientes
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie os clientes dos seus produtos
          </p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Refine sua busca</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por cliente ou produto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Produto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os produtos</SelectItem>
                {products?.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {uniqueCustomers?.map((customer) => (
                  <SelectItem key={customer.id} value={customer.name}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status Calculado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="Ativo">Ativo</SelectItem>
                <SelectItem value="Em Risco">Em Risco</SelectItem>
                <SelectItem value="Churned">Churned</SelectItem>
                <SelectItem value="Sem Transações">Sem Transações</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Clientes</CardTitle>
          <CardDescription>
            {filteredCustomers?.length || 0} cliente(s) encontrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Nome
                        {getSortIcon('name')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('product')}
                    >
                      <div className="flex items-center">
                        Produto
                        {getSortIcon('product')}
                      </div>
                    </TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('city')}
                    >
                      <div className="flex items-center">
                        Cidade
                        {getSortIcon('city')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('state')}
                    >
                      <div className="flex items-center">
                        Estado
                        {getSortIcon('state')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('renewals')}
                    >
                      <div className="flex items-center">
                        Renovações
                        {getSortIcon('renewals')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('firstPurchase')}
                    >
                      <div className="flex items-center">
                        Primeira Compra
                        {getSortIcon('firstPurchase')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('lastTransaction')}
                    >
                      <div className="flex items-center">
                        Última Compra
                        {getSortIcon('lastTransaction')}
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center">
                        Status Calculado
                        <StatusInfoDialog />
                      </div>
                    </TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCustomers?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        Nenhum cliente encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCustomers?.map((customer) => {
                      const calculatedStatus = calculateCustomerStatus({
                        lastTransactionDate: customer.last_transaction_date,
                        periodEndDate: customer.last_transaction?.[0]?.period_end_date || null,
                        recurrenceType: customer.last_transaction?.[0]?.product_plan?.recurrence_type as any || null,
                      });
                      return (
                        <TableRow key={customer.id}>
                          <TableCell className="font-medium">{customer.name}</TableCell>
                          <TableCell>{customer.product?.name || '-'}</TableCell>
                          <TableCell>{customer.last_transaction?.[0]?.product_plan?.name || '-'}</TableCell>
                          <TableCell>{customer.city || '-'}</TableCell>
                          <TableCell>{customer.state || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {customer.total_renewals || 0}x
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {customer.first_purchase_date
                              ? format(new Date(customer.first_purchase_date), 'dd/MM/yyyy', { locale: ptBR })
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {customer.last_transaction_date
                              ? format(new Date(customer.last_transaction_date), 'dd/MM/yyyy', { locale: ptBR })
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={calculatedStatus.variant}>
                              {calculatedStatus.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(customer)}
                            >
                              Editar
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CustomerSaasDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customer={selectedCustomer}
      />
    </div>
  );
}
